import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Stack } from '@mui/material';
import axios from 'axios';
import url from '../serverUrl';

/**
 * Read-only "proof of backing" panel for a token's public detail page.
 * Pulls straight from the public, unauthenticated registry endpoint
 * (/api/v2/treasury-registry) - the same data DeFiLlama-style trackers
 * would read - so this is exactly what an outside auditor sees, not an
 * admin-only view.
 *
 * Always renders its "Treasury Backing" header once tokenType is passed,
 * even while loading / on error / with nothing assigned yet - so the
 * section is never silently invisible (that would be indistinguishable
 * from the feature simply not being wired in).
 *
 * Props:
 *   tokenType - canonical type string (e.g. 'DAT', 'SYNTHETICS', 'STABLECOIN')
 *   tokenId   - the token's numeric id (may be undefined briefly while the
 *               parent page is still fetching the token itself)
 */
function TreasuryBackingPublic({ tokenType, tokenId }) {
  const [entry, setEntry] = useState(null);
  // idle | loading | loaded | error
  const [status, setStatus] = useState('idle');
  const [errorDetail, setErrorDetail] = useState('');

  useEffect(() => {
    if (!tokenType || !tokenId) {
      setEntry(null);
      setStatus('idle');
      return;
    }

    setStatus('loading');
    axios
      .get(`${url}/api/v2/treasury-registry/token/${tokenType}/${tokenId}`)
      .then((res) => {
        setEntry(res.data);
        setStatus('loaded');
      })
      .catch((err) => {
        setEntry(null);
        setStatus('error');
        setErrorDetail(
          err?.response?.status === 404
            ? '' // no treasury assigned yet - not a real error, handled below
            : err?.response?.data?.error || err.message
        );
        // eslint-disable-next-line no-console
        console.error(
          `TreasuryBackingPublic: GET /api/v2/treasury-registry/token/${tokenType}/${tokenId} failed`,
          err
        );
      });
  }, [tokenType, tokenId]);

  if (!tokenType) return null;

  const hasBackingAssets = entry?.backing_assets?.length > 0;

  return (
    <Box
      sx={{
        border: '0px solid #10B981',
        borderRadius: 2,
        p: 2.5,
        backgroundColor: 'rgba(16, 185, 129, 0.03)'
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: '#10B981',
          mb: 2,
          fontWeight: 700,
          textTransform: 'uppercase',
          fontSize: '0.75rem'
        }}
      >
        Treasury Backing
      </Typography>

      {status === 'idle' && (
        <Typography variant="body2" sx={{ color: '#9ca3af' }}>
          Loading token...
        </Typography>
      )}

      {status === 'loading' && (
        <Typography variant="body2" sx={{ color: '#9ca3af' }}>
          Loading treasury data...
        </Typography>
      )}

      {status === 'error' && errorDetail && (
        <Typography variant="body2" sx={{ color: '#EF5350' }}>
          Could not load treasury data: {errorDetail}
        </Typography>
      )}

      {(status === 'loaded' || (status === 'error' && !errorDetail)) &&
        !hasBackingAssets && (
          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
            No treasury currently backs this token.
          </Typography>
        )}

      {hasBackingAssets && (
        <Stack spacing={2}>
          {entry.backing_assets.map((b, i) => (
            <Box
              key={b.treasury_id || i}
              sx={{
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 1.5,
                p: 1.5
              }}
            >
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                {b.asset}: {b.amount}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: '#9ca3af', wordBreak: 'break-all', display: 'block', mb: 1 }}
              >
                Treasury: {b.treasury_id} ({b.treasury_address})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {b.lock_status &&
                  Object.entries(b.lock_status).map(([key, value]) => (
                    <Chip
                      key={key}
                      size="small"
                      label={`${key}: ${value ?? 'N/A'}`}
                      sx={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10B981' }}
                    />
                  ))}
              </Box>
            </Box>
          ))}

          <Typography variant="caption" sx={{ color: '#6b7280', wordBreak: 'break-all' }}>
            Total supply: {entry.total_supply} · Verification hash: {entry.verification_hash}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

export default TreasuryBackingPublic;
